import React, { useEffect, useMemo, useState } from 'react';
import { Box, Button, MenuItem, Stack, TextField, Typography } from '@mui/material';
import { StagedSourceBadge } from '../StagedSourceBadge';
import { DateField } from '../ui/DateField';
import { InlineMessage } from '../ui/InlineMessage';
import { StatusChip } from '../ui/StatusChip';
import {
  EMPTY_LAUNCH_VALUE,
  fieldProvenanceChip,
  formatLaunchDateValue,
  lifecycleFromLaunchDates,
  type DatedFieldProvenanceValue,
  type LaunchDatesForLifecycle,
} from './launchDateDisplay';

/**
 * Admin editor for a product's pre-release lifecycle (flag `preorder_lifecycle`,
 * MOT-143), shared by the canonical and staged product detail views. Shows the
 * stored stage with its dates and lets Alex set or clear them. The card's own
 * Save writes immediately; a dirty draft is also included in the panel's main
 * Save. The server re-reads the stage from the dates on save, so a release date
 * that has passed saves as Released whatever was picked. `onSave(null)` clears
 * the lifecycle so the product reads its launch dates alone again.
 */
export interface ProductLifecycleValue {
  stage: 'announced' | 'preorder' | 'released';
  releaseDate?: string | null;
  preorderOpensAt?: string | null;
  source?: string;
  updatedAt?: string;
  stageProvenance?: DatedFieldProvenanceValue;
  releaseDateProvenance?: DatedFieldProvenanceValue;
  preorderOpensAtProvenance?: DatedFieldProvenanceValue;
  lastTransition?: { from: string; to: string; at?: string; by?: string };
}

export interface ProductLifecycleInput {
  stage: 'announced' | 'preorder' | 'released';
  releaseDate: string | null;
  preorderOpensAt: string | null;
}

interface Props {
  lifecycle: ProductLifecycleValue | null | undefined;
  /**
   * The product's launch dates. With no stored lifecycle they are read for display (a product
   * promoted as announced, with only `launchDates`, still shows its stage); nothing is saved
   * from them and the form stays empty until the lifecycle is set.
   */
  launchDates?: LaunchDatesForLifecycle | null;
  onSave: (input: ProductLifecycleInput | null) => Promise<void>;
  /** Reports the unsaved edit so the panel's main Save can persist it too. */
  onPendingChange?: (pending: ProductLifecycleInput | null | undefined) => void;
  /** True while the dates are out of order, so the panel Save can stay blocked. */
  onInvalidChange?: (invalid: boolean) => void;
  /** When false, show saved values read-only (matches the panel edit toggle). */
  editMode?: boolean;
  disabled?: boolean;
}

const STAGE_LABEL: Record<ProductLifecycleValue['stage'], string> = {
  announced: 'Announced',
  preorder: 'Pre-order',
  released: 'Released',
};

const STAGE_TONE: Record<ProductLifecycleValue['stage'], 'info' | 'caution' | 'positive'> = {
  announced: 'info',
  preorder: 'caution',
  released: 'positive',
};

const dayOf = (value: string | null | undefined): string => (value ? value.slice(0, 10) : '');

const ReadOnlyLifecycleRow: React.FC<{
  label: string;
  value: string;
  provenance: ReturnType<typeof fieldProvenanceChip>;
}> = ({ label, value, provenance }) => (
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'minmax(120px, 160px) minmax(0, 1fr)' },
      gap: 1,
      alignItems: 'start',
      py: 0.75,
      borderTop: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, pt: 0.25 }}>
      {label}
    </Typography>
    <Stack direction="row" alignItems="center" spacing={0.75} flexWrap="wrap" useFlexGap>
      <Typography variant="body2" color={value === EMPTY_LAUNCH_VALUE ? 'text.disabled' : 'text.primary'}>
        {value}
      </Typography>
      {provenance && value !== EMPTY_LAUNCH_VALUE ? (
        <StagedSourceBadge source={provenance} label={provenance.detail?.split(' · ')[0]} />
      ) : null}
    </Stack>
  </Box>
);

export const ProductLifecycleEditor: React.FC<Props> = ({ lifecycle, launchDates, onSave, onPendingChange, onInvalidChange, editMode = false, disabled = false }) => {
  const [stage, setStage] = useState<ProductLifecycleValue['stage']>(lifecycle?.stage || 'announced');
  const [releaseDate, setReleaseDate] = useState(dayOf(lifecycle?.releaseDate));
  const [preorderOpensAt, setPreorderOpensAt] = useState(dayOf(lifecycle?.preorderOpensAt));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setStage(lifecycle?.stage || 'announced');
    setReleaseDate(dayOf(lifecycle?.releaseDate));
    setPreorderOpensAt(dayOf(lifecycle?.preorderOpensAt));
    setError(null);
  }, [lifecycle?.stage, lifecycle?.releaseDate, lifecycle?.preorderOpensAt, editMode]);

  const datesInvalid = !!releaseDate && !!preorderOpensAt && preorderOpensAt > releaseDate;
  const dirty =
    stage !== (lifecycle?.stage || 'announced') ||
    releaseDate !== dayOf(lifecycle?.releaseDate) ||
    preorderOpensAt !== dayOf(lifecycle?.preorderOpensAt);

  const run = async (input: ProductLifecycleInput | null) => {
    setSaving(true);
    setError(null);
    try {
      await onSave(input);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save the lifecycle');
    } finally {
      setSaving(false);
    }
  };

  useEffect(() => {
    if (!onPendingChange) return;
    if (!editMode || !dirty || datesInvalid) {
      onPendingChange(undefined);
      return;
    }
    const cleared = stage === 'announced' && !releaseDate && !preorderOpensAt && !lifecycle;
    onPendingChange(
      cleared
        ? undefined
        : { stage, releaseDate: releaseDate || null, preorderOpensAt: preorderOpensAt || null },
    );
  }, [editMode, dirty, datesInvalid, stage, releaseDate, preorderOpensAt, lifecycle, onPendingChange]);

  useEffect(() => {
    onInvalidChange?.(Boolean(editMode && dirty && datesInvalid));
  }, [onInvalidChange, editMode, dirty, datesInvalid]);

  const cardChip = fieldProvenanceChip(lifecycle?.stageProvenance, lifecycle?.source);
  const derived = useMemo(() => (lifecycle ? null : lifecycleFromLaunchDates(launchDates)), [lifecycle, launchDates]);

  const transitionNote = lifecycle?.lastTransition
    ? `${STAGE_LABEL[lifecycle.lastTransition.from as ProductLifecycleValue['stage']] || lifecycle.lastTransition.from} → ${STAGE_LABEL[lifecycle.lastTransition.to as ProductLifecycleValue['stage']] || lifecycle.lastTransition.to} by ${lifecycle.lastTransition.by === 'sweep' ? 'the daily sweep' : 'an admin'}`
    : '';

  const header = (
    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
      {lifecycle ? (
        <>
          <StatusChip size="small" tone={STAGE_TONE[lifecycle.stage]} label={STAGE_LABEL[lifecycle.stage]} />
          {cardChip ? <StagedSourceBadge source={cardChip} label={cardChip.detail?.split(' · ')[0]} /> : null}
          {transitionNote ? (
            <Typography variant="caption" color="text.secondary">
              {transitionNote}
            </Typography>
          ) : null}
        </>
      ) : derived ? (
        <>
          <StatusChip size="small" tone={STAGE_TONE[derived.stage]} label={STAGE_LABEL[derived.stage]} />
          <Typography variant="caption" color="text.secondary">
            Read from the launch dates; no lifecycle is saved, so stores still see the product as today. Set one to list it
            ahead of launch for stores with the pre-release lifecycle turned on.
          </Typography>
        </>
      ) : (
        <Typography variant="caption" color="text.secondary">
          No lifecycle recorded: the product reads as released, and stores see it as today. Set one to list it ahead of launch
          for stores with the pre-release lifecycle turned on.
        </Typography>
      )}
    </Stack>
  );

  if (!editMode) {
    const stageLabel = lifecycle ? STAGE_LABEL[lifecycle.stage] : derived ? STAGE_LABEL[derived.stage] : EMPTY_LAUNCH_VALUE;
    const preorder = lifecycle?.preorderOpensAt
      ? formatLaunchDateValue(dayOf(lifecycle.preorderOpensAt), 'exact')
      : EMPTY_LAUNCH_VALUE;
    const shownRelease = lifecycle?.releaseDate ?? derived?.releaseDate;
    const release = shownRelease ? formatLaunchDateValue(dayOf(shownRelease), 'exact') : EMPTY_LAUNCH_VALUE;
    return (
      <Stack spacing={1}>
        {header}
        <ReadOnlyLifecycleRow
          label="Stage"
          value={stageLabel}
          provenance={
            lifecycle
              ? fieldProvenanceChip(lifecycle.stageProvenance, lifecycle.source)
              : derived
                ? fieldProvenanceChip(undefined, derived.source)
                : null
          }
        />
        <ReadOnlyLifecycleRow
          label="Pre-orders open"
          value={preorder}
          provenance={lifecycle?.preorderOpensAt ? fieldProvenanceChip(lifecycle.preorderOpensAtProvenance, lifecycle.source) : null}
        />
        <ReadOnlyLifecycleRow
          label="Release date"
          value={release}
          provenance={
            lifecycle?.releaseDate
              ? fieldProvenanceChip(lifecycle.releaseDateProvenance, lifecycle.source)
              : derived?.releaseDate
                ? fieldProvenanceChip(undefined, derived.source)
                : null
          }
        />
      </Stack>
    );
  }

  return (
    <Stack spacing={1.5}>
      {header}
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
        <TextField
          select
          size="small"
          label="Stage"
          value={stage}
          disabled={disabled || saving}
          onChange={(e) => setStage(e.target.value as ProductLifecycleValue['stage'])}
          sx={{ minWidth: 160 }}
          slotProps={{ htmlInput: { 'aria-label': 'Lifecycle stage' } }}
        >
          {(Object.keys(STAGE_LABEL) as Array<ProductLifecycleValue['stage']>).map((s) => (
            <MenuItem key={s} value={s}>{STAGE_LABEL[s]}</MenuItem>
          ))}
        </TextField>
        <DateField
          size="small"
          label="Pre-orders open"
          aria-label="Pre-orders open"
          value={preorderOpensAt}
          onChange={setPreorderOpensAt}
          disabled={disabled || saving}
          error={datesInvalid}
          helperText={datesInvalid ? 'Must be on or before the release date' : 'Flips Announced to Pre-order'}
          sx={{ flex: 1 }}
        />
        <DateField
          size="small"
          label="Release date"
          aria-label="Release date"
          value={releaseDate}
          onChange={setReleaseDate}
          disabled={disabled || saving}
          helperText="Flips to Released; the pre-order button follows"
          sx={{ flex: 1 }}
        />
      </Stack>
      {error && <InlineMessage severity="error">{error}</InlineMessage>}
      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button
          size="small"
          variant="contained"
          disabled={disabled || saving || !dirty || datesInvalid}
          onClick={() => run({ stage, releaseDate: releaseDate || null, preorderOpensAt: preorderOpensAt || null })}
        >
          {saving ? 'Saving…' : 'Save lifecycle'}
        </Button>
        {lifecycle && (
          <Button size="small" color="inherit" disabled={disabled || saving} onClick={() => run(null)}>
            Clear lifecycle
          </Button>
        )}
      </Box>
    </Stack>
  );
};

export default ProductLifecycleEditor;
